import type { MapLayerMouseEvent } from 'maplibre-gl'
import { useAppStore } from '~/hooks'
import { MapTooltipMachineContext } from '~/contexts/MapTooltipMachineContext'
import { hasMapTooltipTarget, queryMapTooltipAtPoint } from '~/util/mapTooltipQuery'

/**
 * Custom hook to handle mouse move events on a map and manage hover state.
 * @returns An object containing the onMouseMove handler.
 */
function useMapHover(): {
  onMouseMove: (event: MapLayerMouseEvent) => void
} {
  const { mapRef, deckRef, DEM, returnPeriod, adaptation, contourFloodEnabled } = useAppStore()
  const { send } = MapTooltipMachineContext.useActorRef()

  const onMouseMove = (event: MapLayerMouseEvent) => {
    if (!event.point) {
      return
    }

    const hit = queryMapTooltipAtPoint({
      mapRef,
      deckRef,
      point: event.point,
      DEM,
      returnPeriod,
      adaptation,
      contourFloodEnabled,
    })

    if (mapRef?.getMap()) {
      const canvas = mapRef.getMap().getCanvasContainer()
      if (canvas) {
        canvas.style.cursor = hasMapTooltipTarget(hit) ? 'pointer' : ''
      }
    }

    send({
      type: 'MOUSE_MOVE',
      x: event.point.x,
      y: event.point.y,
      lat: event.lngLat.lat,
      lng: event.lngLat.lng,
      ...hit,
    })
  }

  return { onMouseMove }
}

export default useMapHover
